<script setup lang="ts">
import {computed} from "vue";
import dateHelper from "@/core/helpers/dateHelper";
import {GiftValidityDays} from "@/birthday-gift/interfaces";

const props = defineProps<{
    pharmacyName: string;
    validityDays: GiftValidityDays;
}>();

const expiryLabel = computed(() => {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + props.validityDays);

    return dateHelper.shortFormat(expiry, 'D MMMM');
});

/**
 * A decorative QR placeholder: the real code is generated by the server
 * and only ever rendered on the patient's phone.
 */
const cells = Array.from({length: 25 * 25}, (_, index) => {
    const x = index % 25;
    const y = Math.floor(index / 25);
    const isFinder = (x < 8 && y < 8) || (x > 16 && y < 8) || (x < 8 && y > 16);

    return {x, y, isFinder, filled: !isFinder && (x * 7 + y * 13 + x * y) % 3 === 0};
});
</script>

<template>
  <div class="gradient flex flex-1 flex-col p-3.5">
    <div class="mb-3 text-xs font-semibold text-white">
      Mon QR code
    </div>
    <div class="flex flex-col items-center overflow-hidden rounded-[18px] bg-white">
      <div class="flex flex-col items-center px-4 pt-4">
        <div class="text-center text-sm font-bold text-primary">
          {{ pharmacyName }}
        </div>
        <div class="my-1 text-[10px] text-muted-foreground">
          Votre cadeau surprise
        </div>
        <div class="rounded-[14px] bg-primary-lightest p-2">
          <svg
            viewBox="0 0 25 25"
            class="h-[110px] w-[110px]"
            shape-rendering="crispEdges"
            role="img"
            aria-label="Aperçu du QR code"
          >
            <g fill="#1c1330">
              <template
                v-for="cell in cells"
                :key="`${cell.x}-${cell.y}`"
              >
                <rect
                  v-if="cell.filled"
                  :x="cell.x"
                  :y="cell.y"
                  width="1"
                  height="1"
                />
              </template>
              <template
                v-for="corner in [[0, 0], [18, 0], [0, 18]]"
                :key="corner.join()"
              >
                <rect
                  :x="corner[0]"
                  :y="corner[1]"
                  width="7"
                  height="7"
                />
                <rect
                  :x="corner[0] + 1"
                  :y="corner[1] + 1"
                  width="5"
                  height="5"
                  fill="#fff"
                />
                <rect
                  :x="corner[0] + 2"
                  :y="corner[1] + 2"
                  width="3"
                  height="3"
                />
              </template>
            </g>
          </svg>
        </div>
        <div class="mb-3 mt-2.5 text-[10px] text-muted-foreground">
          Code à usage unique
        </div>
      </div>
      <div class="w-full bg-secondary p-2.5 text-center text-[11px] font-semibold text-primary-dark">
        À montrer au comptoir avant le {{ expiryLabel }}
      </div>
    </div>
    <div class="mt-auto pt-3 text-center text-[10px] leading-relaxed text-white/80">
      Votre cadeau vous sera révélé au comptoir 🎁
    </div>
  </div>
</template>
